import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  BookOpenIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  PaperClipIcon,
  PencilIcon,
  PlusIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import { runExplorerPath } from '@/components/runExplorer/runExplorerLinks';
import { MarkdownDisplay } from '@/components/shared/MarkdownDisplay';
import { QuestionForm, ResultCard, SpecPreview } from '@/components/composer/ComposerMessage';
import { SpecMetadataChanges } from '@/components/composer/SpecMetadataChanges';
import { useEndpoint } from '@/hooks/useEndpoints';
import { endpointDisplayName } from '@/utils/endpointLabel';
import PlanRequiredNotice from '@/components/ui/PlanRequiredNotice';
import { useRequiresPlan } from '@/hooks/useRequiresPlan';
import { useResourceContextLabel } from '@/hooks/useResourceContextLabel';
import { useAssistant } from '@/providers/AssistantProvider';
import { DISMISS_OPTION_VALUE } from '@/types/composer';
import { useFlag } from '@/hooks/useFeatureFlags';
import { isKnowledgeDocumentSettling } from '@/hooks/useKnowledge';
import { useKnowledgeDocumentUpload } from '@/hooks/useKnowledgeDocumentUpload';
import { SidebarDocumentPicker } from '@/components/knowledge/SidebarDocumentPicker';
import { KNOWLEDGE_UPLOAD_ACCEPT } from '@/config';
import { currentLocationTag, trackEvent } from '@/lib/analytics';
import {
  ASSISTANT_HISTORY_MAX_HEIGHT,
  ASSISTANT_HISTORY_MIN_HEIGHT,
  ASSISTANT_HISTORY_ROW_HEIGHT,
  ASSISTANT_SIDEBAR_MAX_WIDTH,
  ASSISTANT_SIDEBAR_MIN_WIDTH,
  useAssistantHistoryPreference,
  useAssistantSidebarPreference,
} from '@/contexts/UIPreferencesContext';
import { relativeThreadTime, threadDayGroup, type ThreadDayGroup } from '@/utils/relativeThreadTime';
import { docsGuideLink } from './HelpMenu';
import { AssistantGreeting } from './AssistantGreeting';
import { ArtifactCard } from '@/components/artifacts/ArtifactCard';
import { AssistantCriteriaCard, MoreTestsOffer, RunTheseTestsAction } from './AssistantTestingCards';
import { SPECIALIST_LABELS } from '@/types/assistant';
import type { AssistantThreadSummary, AssistantTurn } from '@/types/assistant';
import type { ArtifactReference } from '@/types/artifacts';

/**
 * The ever-present Assistant sidebar (task 720).
 *
 * One conversation, three presentations, chosen by breakpoint:
 *
 *   | ≥ 1100px | docked `aside`, pushes content, collapsible to a ~52px rail,
 *   |          | drag-resizable — and deliberately NON-modal, because the whole
 *   |          | point is watching the Assistant's work land in the page beside
 *   |          | the chat.
 *   | 768–1099 | overlay drawer with a backdrop, launched from the top nav.
 *   | < 768px  | full-screen sheet, launched from a floating button.
 *
 * The two overlay presentations keep dialog semantics — focus trap, Escape
 * closes — because there they genuinely are overlays. Only the docked mode
 * drops modality, which is why it is a plain landmark element rather than a
 * `Dialog`.
 *
 * The conversation itself is owned by `AssistantProvider`, above the routes, so
 * none of this container logic can drop it.
 */

/**
 * Which presentation the conversation gets. The first three are chosen by the
 * viewport (`useAssistantPresentation`); `page` is chosen by the ROUTE (task
 * 1117): on the New AI Service page the conversation is the page body, and the
 * docked pane shows only its rail.
 */
export type AssistantPresentation = 'docked' | 'drawer' | 'sheet' | 'page';

const DOCKED_MIN_WIDTH = 1100;
const SHEET_MAX_WIDTH = 767;

const presentationForWidth = (width: number): AssistantPresentation => {
  if (width >= DOCKED_MIN_WIDTH) return 'docked';
  if (width <= SHEET_MAX_WIDTH) return 'sheet';
  return 'drawer';
};

/**
 * Breakpoint hook driven by `window.innerWidth` rather than `matchMedia`: three
 * bands from one measurement, and one resize listener instead of two media
 * queries that can disagree at the boundary.
 */
export function useAssistantPresentation(): AssistantPresentation {
  const [presentation, setPresentation] = useState<AssistantPresentation>(() =>
    typeof window === 'undefined' ? 'docked' : presentationForWidth(window.innerWidth)
  );

  useEffect(() => {
    const onResize = () => setPresentation(presentationForWidth(window.innerWidth));
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return presentation;
}

/**
 * Turn a route tag into the context chip's label for a NON-resource page
 * (task 772). Resource pages name the resource itself instead — see
 * `useResourceContextLabel`.
 */
export function locationLabel(tag: string): string {
  if (!tag || tag === '/') return 'Dashboard';
  const [, first, second] = tag.split('/');
  const pretty = (segment: string) =>
    segment
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  if (!first) return 'Dashboard';
  if (second === ':id') return `${pretty(first)} detail`;
  return pretty(first);
}

/**
 * The context chip — what this conversation is ABOUT (tasks 772, 775).
 *
 * Extracted so both header owners can render it: in the docked pane it belongs
 * on the ✦ row (which `DockedSidebar` owns), while the overlay presentations
 * have no ✦ row and keep it in the conversation's own header.
 *
 * Styled as PLAIN TEXT rather than a pill (task 775): a bordered chip on the
 * same line as the mark reads as a control the User can press, and it is not
 * one — it is a label saying where the conversation is pointed. The row's
 * bottom border is the only separation the header needs.
 */
function AssistantContextChip() {
  const { context } = useAssistant();
  const { label: resourceLabel, fullName: resourceName } = useResourceContextLabel(
    context.resourceType,
    context.resourceId
  );

  const text = resourceLabel ?? locationLabel(context.location);
  // Only worth a tooltip when a resolved name is what might be truncated.
  const fullTitle = resourceName ? (resourceLabel ?? undefined) : undefined;

  return (
    <span
      data-testid="assistant-context-chip"
      title={fullTitle}
      /**
       * `block` is load-bearing (task 778). A `<span>` is `display: inline` by
       * default, and `overflow`, `width` and `min-width` do not apply to an
       * inline box — so `truncate` silently does nothing and a long name runs
       * on, painting straight over the controls beside it. Task 772's pill
       * carried `inline-flex`, which made it a constraining box by accident;
       * task 775's plain-text restyle dropped that with the rest of the pill
       * classes and took the truncation with it.
       */
      className="block min-w-0 truncate text-xs text-gray-500 dark:text-gray-400"
    >
      {text}
    </span>
  );
}

/**
 * New chat, the conversation-level control (task 775).
 *
 * Extracted alongside the chip so the docked header can hold the whole row —
 * `✦ · chip · [new chat]` — while the overlays keep it in the conversation
 * header. The gate is unchanged: New chat only when there is a chat to leave
 * (task 765). The history toggle that sat beside it is gone (task 1202): the
 * History section under the chat box owns the list now.
 */
function AssistantConversationControls() {
  const { threads, turns, newChat } = useAssistant();
  const hasConversationToLeave = turns.length > 0 || threads.length > 0;

  /**
   * Both controls carry an OPAQUE resting background matching the pane (task
   * 778). They sit at the end of a row whose other cell is a truncating label,
   * and a transparent control lets whatever is behind it read through — which
   * is exactly what made them unreadable in kv's screenshot. The chip's
   * truncation is the real fix; this is the belt to its braces.
   */
  return (
    <div className="flex flex-shrink-0 items-center gap-1">
      {hasConversationToLeave && (
        <button
          type="button"
          onClick={newChat}
          data-testid="assistant-new-chat"
          /**
           * Icon-only (task 779). The visible "New chat" label made this the widest thing on a header row
           * whose scarcest resource is width for the context chip; `title` is
           * the hover hint, and the `sr-only` label below is what keeps the
           * accessible name once the visible text is gone.
           */
          title="New chat"
          className="rounded-md bg-white p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 dark:hover:text-gray-200"
        >
          <PlusIcon className="h-4 w-4" />
          <span className="sr-only">New chat</span>
        </button>
      )}
    </div>
  );
}

/**
 * What the library records about a file attached here (task 970).
 *
 * The upload goes through the ordinary documents route, so the file lands in the
 * account's own library, unfiled, exactly as one added on the Documents page
 * does. Saying where it came from is what keeps that library legible once a few
 * conversations have left files in it.
 */
const ATTACHMENT_DESCRIPTION = 'Attached in an Innovate Assistant conversation';


// ---------------------------------------------------------------------------
// Conversation — shared by all three presentations
// ---------------------------------------------------------------------------

interface AssistantConversationProps {
  /**
   * Whether the conversation renders its own header row (task 775).
   *
   * The docked pane already has a header — the ✦ row `DockedSidebar` owns — so
   * it renders the chip and controls there and passes `showHeader={false}`,
   * leaving the conversation as stream + composer. The overlay presentations
   * have no such row and keep their own.
   */
  showHeader?: boolean;
}

/** How long the composer pulse (task 1196) shows; matches `animate-composer-pulse` in tailwind.config.js. */
const COMPOSER_PULSE_MS = 600;

export function AssistantConversation({ showHeader = true }: AssistantConversationProps = {}) {
  const {
    turns,
    isSending,
    error,
    send,
    approveAction,
    revisingArtifact,
    reviseArtifact,
    attachment,
    setAttachment,
    clearAttachment,
    draft,
    consumeDraft,
    composerPulse,
    consumeComposerPulse,
  } = useAssistant();
  // Pre-flight plan gate at the point of intent (task 467 pattern): ANSWER
  // turns are never gated — a User without a plan is the one who most needs
  // help — so this only ever disables an action card's Approve control.
  const requiresPlan = useRequiresPlan();

  /**
   * ATTACHING A DOCUMENT (task 970) — the whole feature behind one flag, so off
   * leaves the composer exactly as it was: no paperclip, no drop target, no chip
   * and no Send rule of its own.
   */
  const attachEnabled = useFlag('innovate.fe.assistant.documentAttach');
  /**
   * The same flag `PlanRequiredNotice` reads (task 1018). Read here too because
   * the upload error uses the notice AS its whole error text: with the notices
   * off the component renders nothing, which would leave an empty amber line.
   * Off, the refusal falls back to `uploadError.message` — already the friendly
   * `no_active_plan` copy from `friendlyApiErrorMessage`.
   */
  const planNoticesEnabled = useFlag('innovate.fe.plan.no-plan-notices');
  /**
   * THE HISTORY SECTION's own lever (task 1217): the thread list with resume,
   * rename and archive. Evaluated under `innovate.fe.help.assistant`; off, the
   * conversation column ends at the composer.
   */
  const historyEnabled = useFlag('innovate.fe.assistant.history');
  /**
   * Whether the last upload was refused for the plan. Attaching is plan-gated
   * even though ANSWERING never is: the upload counts against
   * `max_knowledge_documents` and `max_knowledge_storage_mb`, so the shared
   * upload sequence reports `no_active_plan` / `limit_exceeded` here and the
   * sidebar answers with its needs-a-plan prompt rather than a raw message.
   */
  const [uploadNeedsPlan, setUploadNeedsPlan] = useState(false);
  /**
   * The one upload sequence every surface uses (task 1084): envelope check,
   * `file.name` as the name, and the name-collision prompt — led here by Attach
   * existing, which is what ends the old dead-end on a repeat file.
   */
  const attachUpload = useKnowledgeDocumentUpload({
    surface: 'sidebar',
    description: ATTACHMENT_DESCRIPTION,
    onPlanError: () => setUploadNeedsPlan(true),
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** "Choose from Documents" — attach a document already in the library. */
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  /** A file is over the composer right now — the only reason to highlight it. */
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  /**
   * Why the last upload did not happen. Two shapes, because there are two
   * remedies: a plan refusal is answered by choosing a plan, and everything else
   * is a message the User dismisses and tries again.
   */
  const uploadError: { needsPlan: boolean; message: string } | null = attachUpload.error
    ? { needsPlan: uploadNeedsPlan, message: attachUpload.error }
    : null;
  const dismissUploadError = () => {
    attachUpload.clearError();
    setUploadNeedsPlan(false);
  };

  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** Set when a draft arrives; the layout effect below focuses once it renders. */
  const focusDraftRef = useRef(false);

  /**
   * The input's height follows its text (task 769).
   *
   * Keyed on `input`, so it covers every way the text changes — typing, paste,
   * deleting back down, and the reset after a send (which clears `input`) —
   * rather than only the keystroke path. Collapsing to `auto` before measuring
   * is the load-bearing half: `scrollHeight` on an already-tall field reports
   * the height it currently has, so without the reset the field could only ever
   * grow and would never come back down.
   *
   * `useLayoutEffect` rather than `useEffect` so the measure-and-set happens
   * before paint; on `useEffect` the collapsed frame is briefly visible as a
   * flicker while typing the second line.
   */
  useLayoutEffect(() => {
    const field = inputRef.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${field.scrollHeight}px`;
    // A draft just placed (task 1131): focus with the caret after its text, now
    // that the committed value is the draft's.
    if (focusDraftRef.current) {
      focusDraftRef.current = false;
      field.focus();
      field.setSelectionRange(field.value.length, field.value.length);
    }
  }, [input]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, isSending]);

  /**
   * THE SEND RULE THE ATTACHMENT ADDS (task 970).
   *
   * Narrow on purpose: it applies only while an attachment exists that the
   * service would refuse. The service takes a subject document only once it is
   * `ready` — a document still indexing answers 422 and a failed one never
   * becomes usable — so sending either would spend a turn to be told no. With no
   * attachment, or a Ready one, the composer behaves exactly as it always has.
   *
   * The textarea deliberately stays live throughout: typing the question while
   * the file indexes is the point.
   */
  const attachmentHoldsSend =
    attachEnabled && attachment !== null && attachment.status !== 'ready';

  /**
   * What the composer says when Enter will not send (task 977).
   *
   * The Send button used to carry this in a `title`; with the button gone the
   * hold needs a line of its own, or Enter is simply dead. Null whenever the
   * message can go, so the line only ever appears while something is holding it.
   */
  const sendHint = attachmentHoldsSend
    ? attachment?.status === 'failed'
      ? 'Remove the attachment to send.'
      : 'Waiting for the attached document to finish indexing…'
    : isSending
      ? 'Waiting for the current message to finish…'
      : null;

  const submit = (text: string) => {
    if (!text.trim() || isSending || attachmentHoldsSend) return;
    void send(text);
    setInput('');
  };

  /**
   * Take a file from the picker or from a drop, upload it, and hold it as the
   * conversation's attachment.
   *
   * The upload is the shared sequence (`useKnowledgeDocumentUpload`, task 1084)
   * — the same one the Documents page uses, so the file lands in the account's
   * library under its own name, and a repeat name gets the conflict prompt
   * instead of a dead-end. What is different here is only what happens next:
   * the resolved document becomes the chip, and the provider polls it to Ready.
   * A Discard, or a failure the hook has already put in `uploadError`, resolves
   * `null` and leaves the composer as it was.
   */
  const attachFile = async (file: File) => {
    setUploadNeedsPlan(false);
    trackEvent('assistant_document_attached', { location: currentLocationTag() });

    const document = await attachUpload.upload(file);
    if (!document) return;
    setAttachment({
      id: document.id,
      // The stored document's name: the file's own name on a fresh upload or
      // Attach existing, the numbered name the service chose on Keep both.
      name: document.name,
      // Read through the library's own predicate rather than re-listing the
      // statuses here, so "still ingesting" means one thing in the app. A file
      // small enough to have settled already skips the poll entirely.
      status: isKnowledgeDocumentSettling(document)
        ? 'indexing'
        : document.status === 'ready'
          ? 'ready'
          : 'failed',
    });
  };

  /** The picker and the drop both land here; neither knows about the other. */
  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so choosing the SAME file twice in a row still fires `change`.
    event.target.value = '';
    if (file) void attachFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLTextAreaElement>) => {
    if (!attachEnabled) return;
    event.preventDefault();
    setIsDraggingFile(false);
    // One upload at a time: a second drop must not talk over an open prompt.
    if (attachUpload.uploading) return;
    const file = event.dataTransfer.files?.[0];
    if (file) void attachFile(file);
  };

  /**
   * "Revise this file" (task 828).
   *
   * Two things happen, and they are deliberately separate: the reference becomes
   * the next turn's base — which is what makes the engine append a version
   * rather than open a new artifact — and the composer is seeded with a sentence
   * the User then FINISHES. Seeding rather than sending, because the interesting
   * half is what they want changed, and only they know it.
   */
  const handleRevise = (artifact: ArtifactReference) => {
    reviseArtifact(artifact);
    setInput((current) => (current.trim() ? current : `Revise ${artifact.name}: `));
    inputRef.current?.focus();
  };

  /**
   * A draft handed over from outside the sidebar (task 1131), for example the
   * Interface tab's "Create an interface". Taken once — on mount when the
   * sidebar opens with it, or when a new draft arrives while open — placed in
   * the input with the caret at its end, focused, never sent. Consuming it
   * leaves the text as the composer's own state, so editing or sending it is
   * ordinary typing.
   */
  useEffect(() => {
    if (!draft) return;
    const field = inputRef.current;
    if (field && field.value === draft.text) {
      // Same text already in place: no re-render will come to focus it.
      field.focus();
      field.setSelectionRange(field.value.length, field.value.length);
    } else {
      focusDraftRef.current = true;
      setInput(draft.text);
    }
    consumeDraft();
  }, [draft, consumeDraft]);

  /**
   * THE COMPOSER PULSE (task 1196). A page button that sends a turn for the User
   * (Suggest criteria) asks the composer to mark where the request landed: each
   * nonce is taken once, on mount if the sidebar opened because of the click,
   * and answered with a one-cycle ring for `COMPOSER_PULSE_MS`. The nonce is
   * copied into local state before it is consumed so clearing the provider's
   * copy cannot cancel the timer; a new nonce restarts it.
   */
  const [pulseKey, setPulseKey] = useState<number | null>(null);
  useEffect(() => {
    if (composerPulse === null) return;
    setPulseKey(composerPulse);
    consumeComposerPulse();
  }, [composerPulse, consumeComposerPulse]);
  useEffect(() => {
    if (pulseKey === null) return;
    const timer = window.setTimeout(() => setPulseKey(null), COMPOSER_PULSE_MS);
    return () => window.clearTimeout(timer);
  }, [pulseKey]);
  const isPulsing = pulseKey !== null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit(input);
    }
  };

  const latest = turns[turns.length - 1];
  const activeQuestions = (latest?.role === 'assistant' && latest.questions) || [];
  /**
   * THE QUICK-REPLY CHIPS (task 998). The guide keeps emitting `options` either
   * way — the server contract does not move — so this is purely whether the
   * fixed Compose it / Change something row is offered. Off, the same approval
   * is typed into the composer, which is what the guide's prose now asks for.
   * The `questions` form below is a separate affordance and is NOT gated: a
   * vagueness form is the guide asking for answers it cannot infer.
   *
   * Two cases (task 1142):
   * 1. A turn WITHOUT a `buildResult` (the guide's plan, Compose it / Change
   *    something) renders its chips only when this flag is on.
   * 2. A turn WITH a `buildResult` renders its chips whatever the flag says.
   *    Those are the builder's after-build offer (Build a web page / Not now,
   *    tasks 1136 and 1137): a yes or no on a new action, and Not now has no
   *    typed equivalent, so hiding them would leave the offer unanswerable.
   */
  const optionChipsEnabled = useFlag('innovate.fe.assistant.optionChips');
  /**
   * "Not now" (task 1137). A chip whose value is the `__dismiss__` sentinel (the
   * web-page offer's decline, seeded by task 1136) is not a turn: it sends
   * nothing and only takes the chips away. Held as the turn it dismissed, so a
   * later turn's chips are unaffected.
   */
  const [dismissedOptionsTurnId, setDismissedOptionsTurnId] = useState<string | null>(null);
  const activeOptions =
    (latest?.role === 'assistant' &&
      (optionChipsEnabled || Boolean(latest.buildResult)) &&
      latest.id !== dismissedOptionsTurnId &&
      latest.options) ||
    [];
  const chooseOption = (value: string) => {
    if (value === DISMISS_OPTION_VALUE) {
      setDismissedOptionsTurnId(latest?.id ?? null);
      return;
    }
    submit(value);
  };

  return (
    <>
      {/* The conversation's own header row — the OVERLAY presentations only
          (task 775). The docked pane renders the chip and these controls on its
          ✦ row instead, so a second row cannot appear beneath it.

          `flex-shrink-0` here and on every sibling below keeps the STREAM the
          only region that gives way under pressure (task 766); without it the
          header and input would compress before the messages did. */}
      {showHeader && (
        <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
          <AssistantContextChip />
          <AssistantConversationControls />
        </div>
      )}

      {/* Stream.
          Deliberately NOT `flex-1` (task 766): with `flex-1` this region always
          claimed every spare pixel, which is what pushed the input to the pane
          floor and left a dead gap under a short conversation. Left at its
          default `flex-initial` it is exactly as tall as its content while that
          fits — so the input sits directly beneath the last message — and
          `min-h-0` lets it shrink past its content height once the conversation
          outgrows the pane, at which point `overflow-y-auto` scrolls the
          messages INSIDE the pane and the input comes to rest on the bottom.
          One flex rule produces both states; nothing measures anything. */}
      <div
        ref={scrollRef}
        data-testid="assistant-stream"
        className="assistant-scroll min-h-0 space-y-4 overflow-y-auto p-3"
      >
        {/* An empty conversation renders nothing here (task 769): the starter
            suggestions and their heading said what the input's placeholder
            already says, and took the pane over before there was a
            conversation. Empty now means header + input, and the task-766 flex
            model puts the input directly under the header. The one exception
            is a new account (task 1053): a client-side greeting, never a turn,
            so it is invisible to the unread mark and gone with the first turn. */}
        {turns.length === 0 && <AssistantGreeting />}
        {turns.map((turn) => (
          <AssistantTurnView
            key={turn.id}
            turn={turn}
            disabled={isSending}
            requiresPlan={requiresPlan}
            onApprove={() => void approveAction(turn.id)}
            onRevise={handleRevise}
          />
        ))}

        {isSending && (
          <p data-testid="assistant-thinking" className="text-sm text-gray-500 dark:text-gray-400">
            ✨ Working on it…
          </p>
        )}

        {error && (
          <div
            data-testid="assistant-error"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </div>

      {/* Interaction area — the latest turn's live affordances */}
      {(activeQuestions.length > 0 || activeOptions.length > 0) && (
        <div
          data-testid="assistant-interaction"
          className="flex-shrink-0 space-y-2 border-t border-gray-200 px-3 py-2 dark:border-gray-700"
        >
          {activeQuestions.length > 0 && (
            <QuestionForm
              key={latest.id}
              questions={activeQuestions}
              onSubmit={submit}
              disabled={isSending}
            />
          )}
          {activeOptions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {activeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={isSending}
                  data-testid="assistant-option"
                  onClick={() => chooseOption(option.value)}
                  className="rounded-full border border-indigo-300 bg-white px-3 py-1.5 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:bg-gray-800 dark:text-indigo-300"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Composer input */}
      <div className="flex-shrink-0 border-t border-gray-200 p-3 dark:border-gray-700">
        {/* What the next turn will revise, while one is pending (task 828). The
            base changes what the turn DOES — a new version of that file rather
            than a new file — so it is stated where the message is written, and
            it can be taken back without clearing the message. */}
        {revisingArtifact && (
          <div
            data-testid="assistant-revising-artifact"
            className="mb-2 flex items-center gap-2 rounded-md bg-indigo-50 px-2 py-1 text-xs text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">
              Revising <span className="font-medium">{revisingArtifact.name}</span> · v
              {revisingArtifact.versionNumber}
            </span>
            <button
              type="button"
              onClick={() => reviseArtifact(null)}
              data-testid="assistant-revising-cancel"
              aria-label="Stop revising this file"
              className="flex-shrink-0 rounded p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-900/60"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {/* THE ATTACHED DOCUMENT (task 970), in the same place and the same shape
            as the revising chip above it: what the next turn will carry, stated
            where the message is written, and removable without clearing the
            message. Unlike the revision base it survives every turn — an
            attached file is the subject of a conversation, not of one message —
            so the only thing that ends it is Remove. */}
        {attachEnabled && attachment && (
          <div
            data-testid="assistant-attachment"
            data-status={attachment.status}
            className={`mb-2 flex items-center gap-2 rounded-md px-2 py-1 text-xs ${
              attachment.status === 'failed'
                ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-200'
            }`}
          >
            {attachment.status === 'indexing' ? (
              <ArrowPathIcon className="h-3.5 w-3.5 flex-shrink-0 animate-spin" aria-hidden="true" />
            ) : attachment.status === 'failed' ? (
              <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            ) : (
              <PaperClipIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            )}
            <span className="min-w-0 flex-1 truncate">
              {/* Opens the document page in the main pane; the sidebar stays
                  put (task 1081). */}
              <Link
                to={`/knowledge-documents/${attachment.id}`}
                data-testid="assistant-attachment-link"
                className="font-medium underline-offset-2 hover:underline"
              >
                {attachment.name}
              </Link>
              {' · '}
              {attachment.status === 'indexing'
                ? 'Indexing…'
                : attachment.status === 'failed'
                  ? 'Could not be indexed'
                  : 'Ready'}
            </span>
            <button
              type="button"
              onClick={clearAttachment}
              data-testid="assistant-attachment-remove"
              aria-label="Remove the attached document"
              className="flex-shrink-0 rounded p-0.5 hover:bg-black/5 dark:hover:bg-white/10"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Why an upload did not happen. A plan refusal gets the platform's own
            needs-a-plan prompt — attaching is plan-gated through the document
            limits even though answering never is — and everything else gets a
            dismissible line. */}
        {attachEnabled && uploadError && (
          <div
            data-testid="assistant-attachment-error"
            className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
          >
            <span className="min-w-0 flex-1">
              {uploadError.needsPlan && planNoticesEnabled ? (
                <PlanRequiredNotice />
              ) : (
                uploadError.message
              )}
            </span>
            <button
              type="button"
              onClick={dismissUploadError}
              data-testid="assistant-attachment-error-dismiss"
              aria-label="Dismiss this message"
              className="flex-shrink-0 rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/60"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* THE COMPOSER (task 977) — one field and nothing beside it.

            The attach control moved INSIDE the field's left padding and the
            Send button is gone: Enter sends, Shift+Enter breaks the line. Both
            moves buy the same thing, width for the message, which is the
            scarcest resource in a docked pane (task 766). The field itself
            keeps the border, radius and background every other input in the app
            uses; the plus is positioned over its reserved left padding rather
            than wrapped in a second bordered box, so there is only ever one
            edge on screen. */}
        <div className="relative">
          {attachEnabled && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={KNOWLEDGE_UPLOAD_ACCEPT}
                onChange={handleFileSelected}
                data-testid="assistant-attach-input"
                className="hidden"
              />
              {/* Named for the ACTION, not the glyph: the plus is the app's own
                  add icon (the same one "New chat" uses), so the accessible
                  name is the only thing that says what adding means here.
                  `bottom` rather than `inset-y` keeps it on the first line as
                  the field grows. Since task 1084 it opens a two-item menu:
                  upload a new file, or attach one already in the library. */}
              <Menu>
                <MenuButton
                  disabled={attachUpload.uploading}
                  data-testid="assistant-attach"
                  aria-label="Attach a document"
                  title="Attach a document"
                  className="absolute bottom-1 left-1.5 inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-600 dark:hover:text-gray-200"
                >
                  <PlusIcon className="h-5 w-5" />
                </MenuButton>
                <MenuItems
                  data-testid="assistant-attach-menu"
                  className="absolute bottom-full left-0 z-50 mb-1 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800"
                >
                  <MenuItem>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="assistant-attach-upload"
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-50 dark:text-gray-300 dark:data-[focus]:bg-gray-700"
                    >
                      Upload a file
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      type="button"
                      onClick={() => setIsPickerOpen(true)}
                      data-testid="assistant-attach-choose"
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-50 dark:text-gray-300 dark:data-[focus]:bg-gray-700"
                    >
                      Choose from Documents
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
              {/* Picking an existing document sets the same attachment a fresh
                  upload does, already Ready: the library row is settled, so the
                  provider has nothing to poll. */}
              <SidebarDocumentPicker
                isOpen={isPickerOpen}
                onClose={() => setIsPickerOpen(false)}
                onSelect={(picked) => {
                  setIsPickerOpen(false);
                  dismissUploadError();
                  setAttachment(picked);
                }}
              />
              {attachUpload.conflictDialog}
            </>
          )}
          {/* `rows={1}` is the floor the effect above grows from;
              `overflow-y-hidden` is what makes the growth the ONLY way the
              field responds to more text — with it hidden there is no second
              scrollbar inside a pane that already has one. The input region
              stays `flex-shrink-0`, so a taller field takes its space from the
              message stream (task 766), never from itself. The left padding is
              the room the plus sits in, and it is only held open when there is
              a plus to sit there. */}
          <textarea
            ref={inputRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            // Dropping a file on the field attaches it (task 970). The field
            // itself is the target because that is where the message is being
            // written; `dragover` must be prevented for a drop to fire at all.
            onDragOver={(event) => {
              if (!attachEnabled) return;
              event.preventDefault();
              setIsDraggingFile(true);
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleDrop}
            rows={1}
            placeholder="Ask, or describe what to build…"
            aria-label="Message Innovate"
            data-testid="assistant-input"
            disabled={isSending}
            aria-describedby={sendHint ? 'assistant-send-hint' : undefined}
            className={`w-full resize-none overflow-y-hidden rounded-md border bg-white py-2 pr-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 dark:bg-gray-700 dark:text-white ${
              attachEnabled ? 'pl-10' : 'pl-3'
            } ${
              isDraggingFile
                ? 'border-indigo-500 ring-2 ring-indigo-400'
                : 'border-gray-300 dark:border-gray-600'
            } ${
              // Task 1196: the ring shows for the pulse's duration either way;
              // only the expanding animation is dropped under reduced motion.
              isPulsing ? 'ring-2 ring-indigo-400 animate-composer-pulse motion-reduce:animate-none' : ''
            }`}
          />
        </div>

        {/* WHY ENTER DID NOTHING (task 977). With no Send button there is no
            disabled control left to carry the reason, and a key that silently
            does nothing is the failure mode this line exists to prevent. */}
        {sendHint && (
          <p
            id="assistant-send-hint"
            data-testid="assistant-send-hint"
            role="status"
            className="mt-1 text-xs text-gray-500 dark:text-gray-400"
          >
            {sendHint}
          </p>
        )}
      </div>

      {/* History (task 1202) — the conversation column's last child, pinned to
          the pane floor by its own `mt-auto` (task 1210), never over the
          transcript. */}
      {historyEnabled && <AssistantHistorySection />}
    </>
  );
}

/** Day labels in the order the History section shows them. */
const HISTORY_GROUPS: ThreadDayGroup[] = ['Today', 'Yesterday', 'Earlier'];

/** Pane height a History drag always leaves for the stream and composer (task 1210). */
const HISTORY_PANE_FLOOR = 160;

/**
 * The History section under the chat box (task 1202), pinned to the pane floor
 * as a resizable drawer (task 1210).
 *
 * Replaces the clock toggle's drop-over panel (tasks 772, 777). The heading is
 * the one control for open/collapsed: chevron, "History" and the count in a
 * single button. Open by default.
 *
 * - No chats in this context, no section (the task-772 rule: no empty state).
 * - The section is the column's last child and `mt-auto` rests it on the pane
 *   floor in every state; idle, the gap under the ask box belongs to it. The
 *   stream stays the only region that shrinks (task 766): the section is
 *   `flex-shrink-0`.
 * - The list is a drawer, not a shrink-wrap: it occupies the stored height
 *   (default four rows, 176px) and scrolls inside it. The handle on the
 *   section's top edge drags it taller or shorter (ArrowUp/ArrowDown move one
 *   row, Home/End jump to the limits), clamped to 88–640px and, at drag time,
 *   to the pane height minus `HISTORY_PANE_FLOOR`, so a drag never hides the
 *   conversation. Hidden while collapsed.
 * - Collapsed and height live in `useAssistantHistoryPreference`, per User
 *   keyed by the Keycloak subject, never in component state, so route changes,
 *   the drawer closing and reopening, and signing out and back in all find the
 *   section as the User left it.
 * - Rows are grouped Today / Yesterday / Earlier by `lastActiveAt` in local
 *   time, newest first; the open thread is highlighted.
 * - Opening a row leaves the section as it is.
 */
function AssistantHistorySection() {
  const { threads, threadId, resumeThread, renameThread, archiveThread } = useAssistant();
  const { collapsed, setCollapsed, height, setHeight } = useAssistantHistoryPreference();
  // Which row is being renamed inline, if any (task 777).
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
  const listId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const draggingRef = useRef(false);

  if (threads.length === 0) return null;

  /** The tallest the list may be right now: the ceiling, or the pane minus the floor. */
  const dragMax = () => {
    const pane = sectionRef.current?.parentElement?.clientHeight ?? 0;
    // An unmeasured pane (0px, e.g. before layout) leaves only the ceiling.
    const paneMax = pane > 0 ? pane - HISTORY_PANE_FLOOR : ASSISTANT_HISTORY_MAX_HEIGHT;
    return Math.max(ASSISTANT_HISTORY_MIN_HEIGHT, Math.min(ASSISTANT_HISTORY_MAX_HEIGHT, paneMax));
  };

  // Follows `startDrag` in `DockedSidebar`: window listeners for the drag's
  // life, a ref for "dragging", the preference hook doing the clamping.
  const startResize = (event: React.MouseEvent) => {
    event.preventDefault();
    draggingRef.current = true;
    const startY = event.clientY;
    const startHeight = height;
    const max = dragMax();
    const onMove = (moveEvent: MouseEvent) => {
      if (!draggingRef.current) return;
      setHeight(Math.min(max, startHeight + (startY - moveEvent.clientY)));
    };
    const onUp = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleResizeKey = (event: React.KeyboardEvent) => {
    const max = dragMax();
    let next: number | null = null;
    if (event.key === 'ArrowUp') next = Math.min(max, height + ASSISTANT_HISTORY_ROW_HEIGHT);
    else if (event.key === 'ArrowDown') next = height - ASSISTANT_HISTORY_ROW_HEIGHT;
    else if (event.key === 'Home') next = ASSISTANT_HISTORY_MIN_HEIGHT;
    else if (event.key === 'End') next = max;
    if (next === null) return;
    event.preventDefault();
    setHeight(next);
  };

  const isOpen = !collapsed;
  const now = new Date();
  const sorted = [...threads].sort(
    (a, b) => (Date.parse(b.lastActiveAt) || 0) - (Date.parse(a.lastActiveAt) || 0)
  );
  const groups = HISTORY_GROUPS.map((label) => ({
    label,
    rows: sorted.filter((thread) => threadDayGroup(thread.lastActiveAt, now) === label),
  })).filter((group) => group.rows.length > 0);

  const renderRow = (thread: AssistantThreadSummary) => {
    const isActive = thread.id === threadId;
    const when = relativeThreadTime(thread.lastActiveAt, now);
    return (
      <li
        key={thread.id}
        className={`group flex items-center gap-1 rounded-md ${
          isActive
            ? 'bg-indigo-50 dark:bg-indigo-900/30'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'
        }`}
      >
        {renamingThreadId === thread.id ? (
          <input
            autoFocus
            type="text"
            data-testid="assistant-history-rename-input"
            defaultValue={thread.title || ''}
            aria-label="Chat name"
            // Enter commits (a blank name does not), Escape and blur leave the
            // stored name untouched.
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const title = event.currentTarget.value.trim();
                if (title && title !== thread.title) void renameThread(thread.id, title);
                setRenamingThreadId(null);
              } else if (event.key === 'Escape') {
                setRenamingThreadId(null);
              }
            }}
            onBlur={() => setRenamingThreadId(null)}
            className="w-full rounded-md border border-primary-400 bg-white px-2 py-1 text-xs text-gray-900 focus:outline-none dark:border-primary-500 dark:bg-gray-800 dark:text-gray-100"
          />
        ) : (
          <>
            <button
              type="button"
              data-testid="assistant-history-item"
              aria-current={isActive ? 'true' : undefined}
              onClick={() => void resumeThread(thread.id)}
              className="flex min-w-0 flex-1 flex-col items-start rounded-md px-2 py-1.5 text-left"
            >
              <span
                data-testid="assistant-history-title"
                className={`block w-full truncate text-xs ${
                  isActive
                    ? 'font-medium text-indigo-700 dark:text-indigo-200'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {thread.title || 'Untitled chat'}
              </span>
              <span className="text-[11px] text-gray-400 dark:text-gray-500">
                {isActive ? (when ? `${when} · open now` : 'open now') : when}
              </span>
            </button>
            {/* Row controls surface on hover or keyboard focus; they stay in
                the layout so the rows never jump. */}
            <button
              type="button"
              data-testid="assistant-history-rename"
              title="Rename chat"
              onClick={() => setRenamingThreadId(thread.id)}
              className="flex-shrink-0 rounded-md p-1 text-gray-400 opacity-0 hover:bg-gray-100 hover:text-gray-600 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            >
              <PencilIcon className="h-3.5 w-3.5" />
              <span className="sr-only">Rename chat</span>
            </button>
            <button
              type="button"
              data-testid="assistant-history-archive"
              title="Archive chat"
              onClick={() => void archiveThread(thread.id)}
              className="mr-1 flex-shrink-0 rounded-md p-1 text-gray-400 opacity-0 hover:bg-gray-100 hover:text-gray-600 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            >
              <ArchiveBoxIcon className="h-3.5 w-3.5" />
              <span className="sr-only">Archive chat</span>
            </button>
          </>
        )}
      </li>
    );
  };

  return (
    <section
      ref={sectionRef}
      data-testid="assistant-history"
      aria-label="History"
      className="mt-auto flex-shrink-0 border-t border-gray-200 dark:border-gray-700"
    >
      {/* Resize handle on the section's top edge (task 1210) — the horizontal
          twin of the docked pane's width handle. */}
      {isOpen && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize History"
          aria-valuenow={height}
          aria-valuemin={ASSISTANT_HISTORY_MIN_HEIGHT}
          aria-valuemax={ASSISTANT_HISTORY_MAX_HEIGHT}
          tabIndex={0}
          data-testid="assistant-history-resize-handle"
          onMouseDown={startResize}
          onKeyDown={handleResizeKey}
          className="h-1 w-full cursor-row-resize hover:bg-indigo-300 focus:bg-indigo-300 focus:outline-none dark:hover:bg-indigo-700 dark:focus:bg-indigo-700"
        />
      )}
      <button
        type="button"
        data-testid="assistant-history-toggle"
        aria-expanded={isOpen}
        aria-controls={listId}
        onClick={() => setCollapsed(isOpen)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200"
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`}
        />
        <span>History</span>
        <span className="font-medium normal-case tracking-normal text-gray-400 dark:text-gray-500">
          · {threads.length}
        </span>
      </button>
      {isOpen && (
        <div
          id={listId}
          data-testid="assistant-history-list"
          style={{ height }}
          className="assistant-scroll overflow-y-auto px-2.5 pb-2.5"
        >
          {groups.map((group) => (
            <div key={group.label}>
              <p
                data-testid="assistant-history-group"
                className="px-2 pb-1 pt-2 text-[11px] text-gray-400 dark:text-gray-500"
              >
                {group.label}
              </p>
              <ul className="space-y-0.5">{group.rows.map(renderRow)}</ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// One turn
// ---------------------------------------------------------------------------

/**
 * What a turn saved, in one line (tasks 763, 794).
 *
 * Artifact-producing tiers persist their work inside the turn, so this reports
 * something the User already owns. A refused save says so plainly rather than
 * letting the generated work look kept.
 */
function savedArtifactSummary(saved: NonNullable<AssistantTurn['savedArtifact']>): string {
  if (!saved.saved) {
    return `Not saved: ${saved.error ?? 'this could not be saved'}.`;
  }
  if (saved.tests?.length) {
    const created = saved.tests.filter((test) => test.action === 'created').length;
    const updated = saved.tests.length - created;
    const parts = [
      created > 0 ? `${created} saved` : null,
      updated > 0 ? `${updated} updated` : null,
    ].filter(Boolean);
    return `${parts.join(', ')} in this service's scenarios.`;
  }
  if (saved.criteria?.length) {
    const created = saved.criteria.filter((criterion) => criterion.action === 'created').length;
    const updated = saved.criteria.length - created;
    const parts = [
      created > 0 ? `${created} saved` : null,
      updated > 0 ? `${updated} updated` : null,
    ].filter(Boolean);
    return `${parts.join(', ')} in this service's criteria.`;
  }
  return `Saved as version ${saved.version ?? 1} of this interface.`;
}

/** A save step's error as a sentence: it arrives without closing punctuation. */
function sentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

interface AssistantTurnViewProps {
  turn: AssistantTurn;
  disabled: boolean;
  requiresPlan: boolean;
  onApprove: () => void;
  /** Take one of this turn's files as the base for the next one (task 828). */
  onRevise?: (artifact: ArtifactReference) => void;
}

function AssistantTurnView({
  turn,
  disabled,
  requiresPlan,
  onApprove,
  onRevise,
}: AssistantTurnViewProps) {
  /**
   * A MODIFY turn is a change to a service that already exists (task 804).
   *
   * The card used to read "New AI Service · Here's what I'll compose" whichever
   * guide produced it, which told the User the wrong thing about their own
   * account on every modify confirmation. The modify guide has its own node id,
   * so the turn already says which conversation it belongs to — this just
   * renders it honestly.
   */
  const isModify = turn.specialist === 'composer-modify';
  const { context } = useAssistant();
  /**
   * THE CONNECT ACTION (task 962). Gated on the page it opens: with
   * `innovate.fe.mcp-connections.page` off the route is not mounted, so an
   * action leading to it would dead-end. The guide's own text still names the
   * MCP Connections page, which is the honest thing to say either way.
   */
  const connectPageEnabled = useFlag('innovate.fe.mcp-connections.page');
  /**
   * THE ACTIONS' own lever (task 1217): Connect, Create knowledge base and Run
   * these scenarios. Each action still needs the page it opens too; since task
   * 1216 a `/new` route also reads its entity's `.create` flag, so the two
   * create actions read that as well rather than dead-ending on a redirect.
   */
  const actionsEnabled = useFlag('innovate.fe.assistant.actions');
  const connectCreateEnabled = useFlag('innovate.fe.mcp-connections.create');
  const connectEnabled = actionsEnabled && connectPageEnabled && connectCreateEnabled;
  /**
   * THE CREATE KNOWLEDGE BASE ACTION (task 1079). Gated on the page it opens,
   * for the same reason as the Connect action: with
   * `innovate.fe.knowledge.bases.page` off the route is not mounted.
   */
  const knowledgeBasesPageEnabled = useFlag('innovate.fe.knowledge.bases.page');
  const knowledgeBasesCreateEnabled = useFlag('innovate.fe.knowledge.bases.create');
  const createKnowledgeBaseEnabled =
    actionsEnabled && knowledgeBasesPageEnabled && knowledgeBasesCreateEnabled;
  /** The criteria card's own lever (task 1217), beside `testingCards` (task 1215). */
  const criteriaCardEnabled = useFlag('innovate.fe.assistant.criteriaCard');
  /**
   * THE SPEC CARD (task 998). Off, a build request is answered by the guide's
   * prose alone and the User approves, corrects or redirects by typing — the
   * guide decides `phase: "building"` from that reply and the build runs in the
   * same turn, so nothing about the act tier depends on this button existing.
   * The turn still CARRIES its `spec`; only the structured restatement of it is
   * withdrawn.
   */
  const specCardEnabled = useFlag('innovate.fe.assistant.specCard');
  /** Who sees View run (task 1151): the flag that mounts the Run Explorer it opens (task 1217). */
  const runExplorerEnabled = useFlag('innovate.fe.run-explorer.page');
  /**
   * THE RESULT CARD (task 1011). Off, a finished build ends the turn with one
   * plain line in the guide's own voice rather than the bordered green panel —
   * which on a modify announced a rename as "Your AI capability is ready", as
   * if something new had been built.
   *
   * A FAILED build is never gated: the red panel carries the error the User has
   * to read, so it renders in both arms.
   */
  const resultCardEnabled = useFlag('innovate.fe.assistant.resultCard');
  /**
   * Where the conversation was when the guide asked for the connection, so the
   * form can offer the way back. The sidebar itself never goes anywhere — it
   * lives above the routes — so this returns the reader to the page they were
   * reading, with the conversation still open beside it.
   */
  const location = useLocation();
  const { fullName: targetName } = useResourceContextLabel(
    context.resourceType,
    context.resourceId
  );
  /**
   * The service as it stands, so a modify card can show what it is CHANGING
   * rather than only what it proposes (task 808). Cached under the same key the
   * service's own page uses, so on a service page this costs nothing; it is
   * only fetched on a modify turn, which is the only card that reads it.
   */
  const { data: targetEndpoint } = useEndpoint(
    isModify && context.resourceType === 'endpoint' ? context.resourceId : undefined
  );

  /**
   * THE PLAIN LINE'S TITLE (task 1011). The built service's own name — set on
   * create since task 1006, and on a modify the name it now has — read through
   * `endpointDisplayName` so a legacy service nobody ever named still labels as
   * something a reader recognises rather than as a blank.
   *
   * Fetched only in the plain-line arm, and only for a build that succeeded:
   * the card arm needs no title, and it already shares this query key with the
   * service's own page, so on a modify it costs nothing. `AssistantProvider`
   * invalidates `['endpoint', builtServiceId]` when the build lands, so what
   * arrives here is the service AFTER the change, not before it.
   */
  const built = turn.buildResult;
  const builtEndpointId = built?.status === 'succeeded' ? built.resourceIds?.endpointId : undefined;
  const { data: builtEndpoint } = useEndpoint(
    !resultCardEnabled && builtEndpointId ? builtEndpointId : undefined
  );
  const builtLabel = endpointDisplayName(
    builtEndpoint ?? (isModify ? targetEndpoint : undefined) ?? { path: built?.endpointPath }
  );
  /**
   * The address, exactly as the card shows it: the service composes the
   * slug-qualified URL (task 385), and `endpointPath` alone carries no account
   * slug, so it is only a last-resort fallback for an older response.
   */
  const callableUrl = built?.endpointUrl ?? (built?.endpointPath ? `/api/${built.endpointPath}` : undefined);
  /** A modify never moves the path, and the caller already has it. */
  const showBuildAddress = !isModify && Boolean(callableUrl);

  /**
   * A UI TURN WITH NOTHING TO SAY (task 1121). When the generator's reply does
   * not parse, the turn carries no `notes` and the save step refuses with an
   * error. The card used to read the `MarkdownDisplay` "No content"
   * placeholder above that error; the error is the only true thing the turn
   * has to say, so it becomes the card's text. A turn that DID produce notes
   * keeps them as its text whatever the save step answered.
   */
  const isUiComposerTurn =
    turn.specialist === 'ui-composer' || turn.specialist === 'ui-composer-revise';
  const saveError =
    turn.savedArtifact && !turn.savedArtifact.saved ? turn.savedArtifact.error : undefined;
  const saveErrorAsText = isUiComposerTurn && !turn.text && Boolean(saveError);

  /** What a criteria turn saved (task 1188); it answers in criteria rather than prose. */
  const savedCriteria = turn.savedArtifact?.saved ? (turn.savedArtifact.criteria ?? []) : [];
  /** The tests a test turn saved, which Run these tests runs (task 1188). */
  const savedTestIds = turn.savedArtifact?.saved ? (turn.savedArtifact.tests ?? []).map((test) => test.id) : [];
  const turnServiceId = context.resourceType === 'endpoint' ? context.resourceId : undefined;

  if (turn.role === 'human') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-indigo-600 px-3 py-2 text-sm text-white">
          {turn.text}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="assistant-answer">
      {/* A test turn answers in scenarios rather than prose, so the text row
          steps aside for the list below rather than rendering an empty bubble. */}
      {(turn.text || (!turn.scenarios?.length && savedCriteria.length === 0)) && (
        <div className="flex gap-2 text-sm text-gray-900 dark:text-gray-100">
          <span className="shrink-0">✨</span>
          <div className="min-w-0 break-words">
            {saveErrorAsText ? (
              <div data-testid="assistant-save-error-text">
                <p>{sentence(saveError ?? '')}</p>
                {/* The raw reply exists but could not be read; it is never
                    shown, only named. */}
                {turn.rawReply && (
                  <p
                    data-testid="assistant-unreadable-reply"
                    className="mt-1 text-xs text-gray-500 dark:text-gray-400"
                  >
                    The generator&apos;s reply could not be read.
                  </p>
                )}
              </div>
            ) : (
              <MarkdownDisplay content={turn.text} bare />
            )}
          </div>
        </div>
      )}

      {turn.sources && turn.sources.length > 0 && (
        <ul className="ml-6 space-y-1">
          {turn.sources.map((source) => (
            <li key={source.slug}>
              <a
                href={docsGuideLink(source.slug)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="assistant-source"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
              >
                <BookOpenIcon className="h-3.5 w-3.5" />
                {source.title}
              </a>
            </li>
          ))}
        </ul>
      )}

      {turn.answered === false && (
        <Link
          to="/support"
          state={{ from: currentLocationTag() }}
          data-testid="assistant-contact-support"
          className="ml-6 inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
        >
          <EnvelopeIcon className="h-3.5 w-3.5" />
          Contact support
        </Link>
      )}

      {/* THE CONNECT ACTION (task 962). The guide has just said, in words, that
          this build needs a system the account has not connected; this is the
          same thing said structurally, so the User does not have to go and find
          the vendor's endpoint and header name for themselves.

          THE PRE-FILL TRAVELS IN ROUTER STATE, NEVER THE URL. Everything here
          is non-secret, but a URL is bookmarked, pasted into a ticket and
          written into every access log on the way — and the moment the shape
          rides the query string, the credential field is one careless change
          away from riding it too. The one thing that is genuinely the User's to
          supply is typed into the form and posted from there. */}
      {connectEnabled && turn.missingConnection && (
        <Link
          to="/mcp-connections/new"
          state={{
            connectionPrefill: turn.missingConnection,
            returnTo: `${location.pathname}${location.search}`,
          }}
          onClick={() =>
            trackEvent('assistant_connection_started', {
              connection: turn.missingConnection?.name ?? '',
            })
          }
          data-testid="assistant-connect"
          className="ml-6 inline-flex items-center gap-1.5 rounded-md border border-indigo-300 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
        >
          <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
          Connect {turn.missingConnection.description}
        </Link>
      )}

      {/* THE CREATE KNOWLEDGE BASE ACTION (task 1079). The guide has just said
          the service needs the User's own material and no base fits; this opens
          the new-base form with the name and description already filled in.
          Same router-state hand-off as the Connect action above, so nothing
          rides the URL. */}
      {createKnowledgeBaseEnabled && turn.missingKnowledgeBase && (
        <Link
          to="/knowledge-bases/new"
          state={{ knowledgeBasePrefill: turn.missingKnowledgeBase }}
          data-testid="assistant-create-knowledge-base"
          className="ml-6 inline-flex items-center gap-1.5 rounded-md border border-indigo-300 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
        >
          <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
          Create knowledge base
        </Link>
      )}

      {/* What the turn SAVED (task 763). Artifact-producing tiers — the UI
          Composer today — persist their work inside the turn, so this reports a
          reference the User already owns rather than offering them a save.
          A refused save says so plainly instead of pretending the work is kept. */}
      {/* The test tier's scenarios (task 794). They are already saved — the
          collaboration writes them during the turn — so this is a list of the
          service's tests, not a set of proposals awaiting a decision. */}
      {turn.scenarios && turn.scenarios.length > 0 && (
        <div
          data-testid="assistant-test-scenarios"
          className="rounded-lg border border-gray-200 p-2 dark:border-gray-700"
        >
          <p className="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
            {turn.scenarios.length} test scenario{turn.scenarios.length === 1 ? '' : 's'} · via{' '}
            {SPECIALIST_LABELS[turn.specialist ?? 'test-composer']}
          </p>
          <ul className="space-y-1">
            {turn.scenarios.map((scenario) => (
              <li key={scenario.name} className="text-xs text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-800 dark:text-gray-200">
                  {scenario.name}
                </span>
                {scenario.description ? ` — ${scenario.description}` : ''}
              </li>
            ))}
          </ul>
          {/* The next step, offered where the tests were made (task 1188). */}
          {actionsEnabled && turnServiceId && savedTestIds.length > 0 && (
            <RunTheseTestsAction turnId={turn.id} endpointId={turnServiceId} testIds={savedTestIds} />
          )}
          {/* Three more, without typing (task 1204): only once tests were saved. */}
          {savedTestIds.length > 0 && <MoreTestsOffer />}
        </div>
      )}

      {/* The criteria tier's saved criteria (task 1188), with the instruction
          each came from. Already saved: the turn wrote them as the service's
          criteria, which the Testing tab's Criteria section lists. */}
      {criteriaCardEnabled && savedCriteria.length > 0 && <AssistantCriteriaCard criteria={savedCriteria} />}

      {turn.savedArtifact && (
        <p
          data-testid="assistant-saved-artifact"
          className="ml-6 text-xs text-gray-500 dark:text-gray-400"
        >
          {/* The error is already the card's text, so the line only states
              the outcome rather than repeating it (task 1121). */}
          {saveErrorAsText ? 'Not saved.' : savedArtifactSummary(turn.savedArtifact)}
        </p>
      )}

      {/* The FILES the turn produced (task 828). A card rather than the JSON the
          reference would otherwise render as: the reference is ids, and what a
          reader wants from a file is its name, its size, and the two things
          they can do with it. Costs no request — everything shown arrived with
          the turn. */}
      {turn.artifacts && turn.artifacts.length > 0 && (
        <div className="space-y-2" data-testid="assistant-turn-artifacts">
          {turn.artifacts.map((artifact) => (
            <ArtifactCard key={artifact.versionId} artifact={artifact} onRevise={onRevise} />
          ))}
        </div>
      )}

      {/* The action card: propose → approve → apply. Nothing mutates the
          account before the User approves it here, in the conversation. */}
      {specCardEnabled && turn.spec && (
        <div
          data-testid="assistant-action-card"
          data-action-state={turn.actionState}
          className="rounded-lg border border-indigo-200 p-2 dark:border-indigo-800"
        >
          <p className="mb-2 text-xs font-medium text-indigo-700 dark:text-indigo-300">
            {isModify
              ? `Update ${targetName ?? 'this AI Service'}`
              : 'New AI Service'}{' '}
            · via {SPECIALIST_LABELS[turn.specialist ?? 'composer']}
          </p>
          <SpecPreview
            spec={turn.spec}
            heading={isModify ? "Here's what I'll change" : undefined}
          />

          {/* Metadata changes, named and quoted (task 808). A modify spec
              re-emits the whole desired end state, so only the fields that are
              actually DIFFERENT show here — which is what makes a plan that
              would change nothing visibly change nothing. */}
          {isModify && (
            <SpecMetadataChanges
              spec={turn.spec}
              current={
                targetEndpoint
                  ? {
                      name: targetEndpoint.name,
                      description: targetEndpoint.description,
                      path: targetEndpoint.path,
                    }
                  : undefined
              }
            />
          )}

          {turn.actionState === 'proposed' && (
            <div className="mt-2">
              <button
                type="button"
                onClick={onApprove}
                disabled={disabled || requiresPlan}
                data-testid="assistant-action-approve"
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Approve &amp; build
              </button>
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Not quite right? Just say what to change.
              </p>
              {requiresPlan && <PlanRequiredNotice className="mt-1.5" />}
            </div>
          )}

          {turn.actionState === 'approved' && (
            <p
              data-testid="assistant-action-progress"
              className="mt-2 text-xs text-gray-500 dark:text-gray-400"
            >
              Building it now…
            </p>
          )}
        </div>
      )}

      {/* What the build PRODUCED (task 1011). The card, or — with
          `innovate.fe.assistant.resultCard` off — one plain line in the same
          message style the rest of the turn is written in, so the conversation
          ends in the guide's voice rather than in a panel. A failure is never
          gated: the red panel is the only place the error is said. */}
      {built &&
        (resultCardEnabled || built.status === 'failed' ? (
          <ResultCard result={built} />
        ) : (
          <p
            data-testid="assistant-build-done"
            className="ml-6 text-sm text-gray-900 dark:text-gray-100"
          >
            Done — <strong className="font-semibold">{builtLabel}</strong>{' '}
            {isModify ? 'is updated' : built.conversational ? 'is ready to chat' : 'is ready'}
            {showBuildAddress && (
              <>
                . Call it at <code className="font-mono">POST {callableUrl}</code>
              </>
            )}
            {builtEndpointId ? (
              <>
                {showBuildAddress ? ', or ' : ' — '}
                <Link
                  to={`/services/${builtEndpointId}`}
                  data-testid="assistant-build-open"
                  className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  open the service
                </Link>
                .
              </>
            ) : (
              '.'
            )}
          </p>
        ))}

      {/* The web-page offer sentence (tasks 1136, 1137), after the Done line or
          the card. Its chips render in the interaction area like any options.
          Kept out of ResultCard itself: the standalone Composer page shares
          that card and has no chips to answer the question with. */}
      {built?.status === 'succeeded' && typeof built.message === 'string' && built.message.trim() && (
        <p
          data-testid="assistant-build-offer"
          className="ml-6 mt-1 text-sm text-gray-900 dark:text-gray-100"
        >
          {built.message}
        </p>
      )}

      {/* View run (task 1151): the run that produced this reply, opened on its
          turn. Shown only to Users who can open the Run Explorer — the link
          adds no access. */}
      {turn.run && runExplorerEnabled && (
        <Link
          to={runExplorerPath(turn.run.collaborationId, turn.run.collaborationId, turn.run.turnIndex)}
          data-testid="assistant-view-run"
          className="ml-6 inline-block text-xs text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 hover:underline"
        >
          View run
        </Link>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentations
// ---------------------------------------------------------------------------

/**
 * The docked header mark (task 750). Deliberately UNNAMED: the surface is
 * Innovate itself speaking, not a helper living inside it, so the sparkles mark
 * stands alone and the feature name reaches screen readers through the
 * landmark's own `aria-label` instead of a visible title.
 */
/**
 * The docked pane is pinned to the VIEWPORT, not to the page (task 766).
 *
 * `sticky top-16` + an explicit `100vh - 4rem` height (the top nav is `h-16`)
 * is deliberately chosen over making the whole two-column shell a fixed-height
 * scroll container: the page keeps its ordinary scrolling, every other surface
 * in the app is untouched, and the conversation still never scrolls away.
 *
 * **This depends on the nav being pinned too** (task 770). The offset assumes
 * the `h-16` band at the top of the window is always the nav's; while the nav
 * still scrolled away, a scrolled page left this pane sitting 4rem down with an
 * empty band above it. `AppLayout`'s `<nav>` is now `sticky top-0 z-30`, so the
 * two form one fixed frame — change either offset and the other must follow.
 * `self-start` opts out of the shell's `items-stretch`, which would otherwise
 * stretch the pane to the height of the page content and reintroduce exactly
 * the behavior this removes.
 */
const PINNED_PANE = 'sticky top-16 self-start h-[calc(100vh-4rem)]';

const HEADER = (
  <div className="min-w-0">
    <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
      <SparklesIcon className="h-4 w-4 text-indigo-500" aria-hidden="true" />
    </span>
  </div>
);

/**
 * The docked sidebar and its collapsed rail. A landmark element, not a dialog:
 * no backdrop, no focus trap, page content reflows beside it.
 */
function DockedSidebar() {
  const { isOpen, close, open, hasUnread } = useAssistant();

  /** Collapse to the rail — the same close + tracking the chevron fired. */
  const collapse = useCallback(() => {
    close();
    trackEvent('assistant_sidebar_toggled', { state: 'collapsed' });
  }, [close]);

  /**
   * The header row collapses the pane (task 778), except where a control on it
   * has its own job. Testing the event's target for an enclosing button keeps
   * that rule with the row that owns it, rather than requiring every control —
   * present and future — to stop propagation on its own behalf.
   */
  const handleHeaderClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).closest('button')) return;
      collapse();
    },
    [collapse]
  );
  const { sidebarState, setSidebarState, width, setWidth } = useAssistantSidebarPreference();
  const draggingRef = useRef(false);

  // Persist the docked open/collapsed choice, so the sidebar comes back the way
  // it was left. Only the docked presentation writes this — collapse-to-rail is
  // a desktop-only concept.
  useEffect(() => {
    const next = isOpen ? 'open' : 'collapsed';
    if (next !== sidebarState) setSidebarState(next);
  }, [isOpen, sidebarState, setSidebarState]);

  const startDrag = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      draggingRef.current = true;
      const onMove = (moveEvent: MouseEvent) => {
        if (!draggingRef.current) return;
        setWidth(moveEvent.clientX);
      };
      const onUp = () => {
        draggingRef.current = false;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [setWidth]
  );

  if (!isOpen) {
    return (
      <aside
        data-testid="assistant-rail"
        aria-label="Innovate Assistant"
        className={`${PINNED_PANE} flex w-[52px] flex-shrink-0 flex-col items-center border-r border-gray-200 bg-white py-3 dark:border-gray-700 dark:bg-gray-800`}
      >
        <button
          type="button"
          onClick={() => {
            open();
            trackEvent('assistant_sidebar_toggled', { state: 'open' });
          }}
          data-testid="assistant-rail-expand"
          aria-label="Open Innovate Assistant"
          className="relative rounded-md p-2 text-gray-500 hover:bg-gray-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          <SparklesIcon className="h-5 w-5" />
          {hasUnread && (
            <span
              data-testid="assistant-unread-dot"
              aria-label="New Innovate Assistant activity"
              className="absolute right-1 top-1 h-2 w-2 rounded-full bg-indigo-500"
            />
          )}
        </button>
      </aside>
    );
  }

  return (
    <aside
      data-testid="assistant-sidebar"
      aria-label="Innovate Assistant"
      style={{ width }}
      className={`${PINNED_PANE} relative flex flex-shrink-0 flex-col border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800`}
    >
      {/* ONE header row (task 775), and the row IS the collapse control (task
          778): `✦ · chip · [new chat]` (the history toggle left for the
          History section under the chat box, task 1202). The `«` chevron is gone —
          it spent header width on something the row itself can do, on a row
          whose scarcest resource is width for the chip.

          Clicking the background collapses; clicking a control does its own
          job. The guard is `closest('button')` rather than `stopPropagation`
          on the controls, so this stays a property of the row that owns the
          behavior instead of something every control must remember to opt out
          of — including controls added later.

          The chip is the only flexible cell (`min-w-0 flex-1`), so a long
          resource name truncates rather than pushing the controls into a
          wrap. */}
      <div
        data-testid="assistant-header"
        onClick={handleHeaderClick}
        title="Collapse Innovate Assistant"
        className="flex flex-shrink-0 cursor-pointer items-center gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700"
      >
        {HEADER}
        <div className="min-w-0 flex-1">
          <AssistantContextChip />
        </div>
        <AssistantConversationControls />
        {/* The chevron's ACCESSIBILITY survives it (task 778). A click target
            with no focusable control would be unreachable by keyboard and
            unnamed to a screen reader, so the capability moves to a real
            button that is visually hidden until focused — Enter and Space
            collapse natively, and it keeps the chevron's accessible name. The
            row cannot itself be a `<button>`: it contains buttons, and nesting
            them is invalid and breaks assistive technology. */}
        <button
          type="button"
          onClick={collapse}
          data-testid="assistant-collapse"
          aria-label="Collapse Innovate Assistant"
          className="sr-only focus:not-sr-only focus:rounded-md focus:bg-white focus:px-2 focus:py-1 focus:text-xs focus:text-gray-600 focus:ring-2 focus:ring-indigo-500 dark:focus:bg-gray-800 dark:focus:text-gray-300"
        >
          Collapse
        </button>
      </div>

      <AssistantConversation showHeader={false} />

      {/* Drag handle — clamped by the preference hook, so a wild drag cannot
          leave the sidebar unusable at either extreme. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Innovate Assistant"
        aria-valuenow={width}
        aria-valuemin={ASSISTANT_SIDEBAR_MIN_WIDTH}
        aria-valuemax={ASSISTANT_SIDEBAR_MAX_WIDTH}
        data-testid="assistant-resize-handle"
        onMouseDown={startDrag}
        className="absolute inset-y-0 right-0 w-1 cursor-col-resize hover:bg-indigo-300 dark:hover:bg-indigo-700"
      />
    </aside>
  );
}

/**
 * The rail on the New AI Service page (task 1117).
 *
 * The conversation is the page body there, so the docked pane stands down to
 * its rail rather than showing the same conversation a second time. It is a
 * placeholder, not a toggle: it renders from the route, never touches `isOpen`
 * or the stored per-User choice, so leaving the page brings the pane back
 * exactly as the User left it.
 */
function PageRail() {
  return (
    <aside
      data-testid="assistant-rail"
      data-presentation="page"
      aria-label="Innovate Assistant"
      className={`${PINNED_PANE} flex w-[52px] flex-shrink-0 flex-col items-center border-r border-gray-200 bg-white py-3 dark:border-gray-700 dark:bg-gray-800`}
    >
      <span
        title="The conversation is on this page"
        className="rounded-md p-2 text-indigo-500 dark:text-indigo-400"
      >
        <SparklesIcon className="h-5 w-5" aria-hidden="true" />
      </span>
    </aside>
  );
}

/** The tablet drawer and mobile sheet — genuine overlays, so genuine dialogs. */
function OverlayAssistant({ presentation }: { presentation: 'drawer' | 'sheet' }) {
  const { isOpen, close } = useAssistant();

  return (
    <Dialog open={isOpen} onClose={close} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />
      <DialogPanel
        data-testid={presentation === 'sheet' ? 'assistant-sheet' : 'assistant-drawer'}
        className={
          presentation === 'sheet'
            ? 'fixed inset-0 flex flex-col bg-white dark:bg-gray-800'
            : 'fixed inset-y-0 left-0 flex w-full max-w-md flex-col bg-white shadow-xl dark:bg-gray-800'
        }
      >
        {/* The overlay presentations are already viewport-bounded (`fixed
            inset-y-0` / `inset-0`), so the conversation's flex rules (task 766)
            give them the same behavior as the docked pane with no extra
            sizing: input under the last message while it fits, input at the
            floor with the stream scrolling internally once it does not. */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-700">
          {/* A dialog must have an accessible name, and this one is the same
              unnamed surface (task 750): the sparkles mark shows, the feature
              name is carried for screen readers only. */}
          <DialogTitle className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
            <SparklesIcon className="h-4 w-4 text-indigo-500" aria-hidden="true" />
            <span className="sr-only">Innovate Assistant</span>
          </DialogTitle>
          <button
            type="button"
            onClick={close}
            data-testid="assistant-close"
            aria-label="Close Innovate Assistant"
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <AssistantConversation />
      </DialogPanel>
    </Dialog>
  );
}

/**
 * The sidebar in whichever presentation the viewport calls for. Rendered inside
 * the layout's two-column shell: docked mode occupies the first column, the
 * overlay modes render a portal and occupy nothing.
 */
export function AssistantSidebar({ presentation }: { presentation: AssistantPresentation }) {
  if (presentation === 'page') return <PageRail />;
  if (presentation === 'docked') return <DockedSidebar />;
  return <OverlayAssistant presentation={presentation} />;
}

/** Top-nav launcher — the tablet entry point. */
export function AssistantNavLauncher() {
  const { toggle, hasUnread } = useAssistant();

  return (
    <button
      type="button"
      onClick={() => {
        toggle();
        trackEvent('assistant_sidebar_toggled', { state: 'open' });
      }}
      data-testid="assistant-nav-launcher"
      aria-label="Open Innovate Assistant"
      className="relative rounded-md p-2 text-gray-500 hover:bg-gray-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-gray-700"
    >
      <SparklesIcon className="h-5 w-5" />
      {hasUnread && (
        <span
          data-testid="assistant-unread-dot"
          aria-label="New Innovate Assistant activity"
          className="absolute right-1 top-1 h-2 w-2 rounded-full bg-indigo-500"
        />
      )}
    </button>
  );
}

/** Floating launcher — the mobile entry point. */
export function AssistantFloatingLauncher() {
  const { toggle, hasUnread } = useAssistant();

  return (
    <button
      type="button"
      onClick={() => {
        toggle();
        trackEvent('assistant_sidebar_toggled', { state: 'open' });
      }}
      data-testid="assistant-floating-launcher"
      aria-label="Open Innovate Assistant"
      className="fixed bottom-5 right-5 z-40 inline-flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg hover:bg-indigo-700"
    >
      <SparklesIcon className="h-6 w-6" />
      {hasUnread && (
        <span
          data-testid="assistant-unread-dot"
          aria-label="New Innovate Assistant activity"
          className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-white"
        />
      )}
    </button>
  );
}

export default AssistantSidebar;
